import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

interface StorageManagerInterface {
  /**
   * [getValue]
   * @description Returns the current value associated with the given key, or
   * null if the given key does not exist.
   * @param {('session'|'local')} storageType
   * @param {string} key
   * @memberof StorageManagerServiceInterface
   */
  getValue(storageType:'session'|'local', key: string):void;
  /**
   * [addValue]
   * @description Sets the value of the pair identified by key to value,
   * creating a new key/value pair if none existed for key previously.
   * @param {('session'|'local')} storageType
   * @param {string} key
   * @param {*} value
   * @memberof StorageManagerInterface
   */
  addValue(storageType: 'session'|'local',key: string, value: unknown): void;
  /**
   * [removeValue]
   * @description Removes the key/value pair with the given key, if a key/value
   * pair with the given key exists.
   * @param {('session'|'local')} storageType
   * @param {string} key
   * @memberof StorageManagerInterface
   */
  removeValue(storageType: 'session'|'local',key: string): void;
  /**
   * [updateValue]
   * @description updates a value or property of one of the attributes already
   * stored in storage
   * @param {('session'|'local')} storageType
   * @param {string} key
   * @param {string} property
   * @param {*} value
   * @memberof StorageManagerInterface
   * @description Use try/catch to receive the error
   */
  updateValue(storageType: 'session'|'local', key: string, property: string, value: unknown): void;
  /**
   * [clearStorage]
   * @description Removes all key/value pairs, if there are any.
   * @param {('session'|'local')} storageType
   * @memberof StorageManagerInterface
   */
  clearStorage(storageType: 'session'|'local'): void
}

@Injectable({
  providedIn: 'root'
})
export class StorageManagerService implements StorageManagerInterface {

  private _session: Storage;
  private _local: Storage;

  constructor() {
    this._session = sessionStorage;
    this._local = localStorage;
  }

  private storageSub = new Subject<string | null>();

  public watchStorage(): Observable<string | null> {
    return this.storageSub.asObservable();
  }

/**
 * [_getStorageType]
 * @description If the storageType is session, return the session storage,
 * otherwise return the local storage.
 * @param {'session'|'local'} storageType - 'session'|'local'
 * @returns The storage type that was passed in.
 */
  private _getStorageType(storageType: 'session'|'local'): Storage {
    return (storageType === 'session') ? this._session : this._local;
  }

  public getValue<T>(
    storageType: 'session'|'local',
    key: string
  ): T | null {
    const value: string | null = this._getStorageType(storageType).getItem(key);
    if(value) {
      return JSON.parse(value);
    }
    return null;
  }

  public getValueToken(
    storageType: 'session'|'local' = 'local',
    key: string,
  ): string | null {
    const toReturn = this._getStorageType(storageType).getItem(key) || null;
    return toReturn;
  }

  public addValue(
    storageType: 'session'|'local',
    key: string,
    value: unknown
  ): void{
    const stringifiedValue: string = JSON.stringify(value);
    this._getStorageType(storageType).setItem(key,stringifiedValue);
    this.storageSub.next(key);
  }

  public addValueToken (
    storageType: 'session'|'local',
    key: string,
    value: string,
  ): void{
    this._getStorageType(storageType).setItem(key,value);
    this.storageSub.next(key);
  }

  public removeValue(
    storageType: 'session'|'local',
    key: string
  ): void {
    this._getStorageType(storageType).removeItem(key);
    this.storageSub.next(key);
  }

  public updateValue(
    storageType: 'session'|'local',
    key: string,
    property: string,
    value: unknown
  ): void {
    const storage: string | null = this._getStorageType(storageType).getItem(key);

    if(storage){
      const tempObj = JSON.parse(storage);
      tempObj[property] = value;
      this.addValue(storageType,key,tempObj);
      return;
    }

    throw new Error("the key doesn't exist");
  }

  public clearStorage(storageType: 'session'|'local'): void{
    this._getStorageType(storageType).clear();
    this.storageSub.next(null);
  }

}
